/**
 * C-04 創作者申請審核佇列 helpers (B-40, contract §1e v2.6 — fields agreed ahead of the contract landing).
 * Pure — no React / Firebase — so node:test can import them.
 */

export type CreatorApplicationStatus = "pending" | "approved" | "rejected";
export type CreatorKind = "actor" | "singer" | "creator" | "other";

export const CREATOR_STATUS_LABELS: Record<CreatorApplicationStatus, string> = {
  pending: "待審核",
  approved: "已核准",
  rejected: "已拒絕",
};
export const CREATOR_STATUS_ORDER: CreatorApplicationStatus[] = ["pending", "approved", "rejected"];
export const creatorStatusVariant = (s: string) => (s === "approved" ? "success" : s === "rejected" ? "danger" : "warning");

export const CREATOR_KIND_LABELS: Record<CreatorKind, string> = {
  actor: "演員",
  singer: "歌手",
  creator: "網紅／創作者",
  other: "其他",
};
export const creatorKindLabel = (kind: string | null | undefined) =>
  kind && kind in CREATOR_KIND_LABELS ? CREATOR_KIND_LABELS[kind as CreatorKind] : kind || "—";

/* ---------- reject ---------- */

export const REJECT_REASON_MIN = 5;
export const REJECT_REASON_MAX = 200;
/** Quick picks; `text` is what is sent (and shown to the applicant), always ≥ REJECT_REASON_MIN. */
export const CREATOR_REJECT_PRESETS: { label: string; text: string }[] = [
  { label: "資料不足", text: "資料不足，請補充可驗證的作品或社群連結後重新申請" },
  { label: "非本人", text: "無法確認為本人，請以本人官方帳號的連結重新申請" },
  { label: "不符合定位", text: "目前不符合 Reel+ 創作者定位，感謝你的申請" },
];

const codePoints = (text: string) => [...text].length;

/** Returns an error message, or null when the reason can be sent. */
export function rejectReasonProblem(reason: string): string | null {
  const n = codePoints(reason.trim());
  if (n < REJECT_REASON_MIN) return `請填寫拒絕原因（至少 ${REJECT_REASON_MIN} 個字）`;
  if (n > REJECT_REASON_MAX) return `拒絕原因最多 ${REJECT_REASON_MAX} 字`;
  return null;
}

/* ---------- approve ---------- */

export const NEW_ARTIST_NAME_MAX = 30;
export const NEW_ARTIST_BIO_MAX = 150;

export type CreatorApproveInput =
  | { mode: "existing"; artistId: string; force?: boolean }
  | { mode: "new"; displayName: string; bio: string };
/** `force: true` (B-45) = staff confirmed adding the applicant as a co-owner of an artist that already has an owner. */
export type CreatorApproveBody =
  | { uid: string; artistId: string; force?: true }
  | { uid: string; newArtist: { displayName: string; bio: string } };

/** Validates the approve dialog and builds the request body; throws Error(message) when invalid. */
export function buildApproveBody(uid: string, input: CreatorApproveInput): CreatorApproveBody {
  if (!uid) throw new Error("缺少申請者 UID");
  if (input.mode === "existing") {
    const artistId = input.artistId.trim();
    if (!artistId) throw new Error("請選擇要綁定的藝人");
    if (artistId.includes("/") || artistId.length > 128) throw new Error("藝人 ID 格式錯誤");
    return input.force ? { uid, artistId, force: true } : { uid, artistId };
  }
  const displayName = input.displayName.trim();
  const bio = input.bio.trim();
  if (!displayName) throw new Error("請填寫藝人名稱");
  if (codePoints(displayName) > NEW_ARTIST_NAME_MAX) throw new Error(`藝人名稱最多 ${NEW_ARTIST_NAME_MAX} 字`);
  if (codePoints(bio) > NEW_ARTIST_BIO_MAX) throw new Error(`簡介最多 ${NEW_ARTIST_BIO_MAX} 字`);
  return { uid, newArtist: { displayName, bio } };
}

/* ---------- B-45: existing owners of the artist being bound ---------- */

export interface ArtistOwner {
  uid: string;
  displayName: string | null;
}
/** Owners other than the applicant (re-approving onto one's own artist is not a co-owner case). */
export const otherOwners = (owners: ArtistOwner[] | undefined, applicantUid: string) => (owners ?? []).filter((o) => o.uid !== applicantUid);
/** "小美（u1）、u2" — shown as「目前擁有者：…」in the approve dialog. */
export const ownersLabel = (owners: ArtistOwner[]) =>
  owners.map((o) => (o.displayName?.trim() ? `${o.displayName.trim()}（${o.uid}）` : o.uid)).join("、");

/** Bio pre-fill for a new artist: the applicant's bio cut to the profile limit. */
export const prefillBio = (bio: string | null | undefined) => [...(bio ?? "").trim()].slice(0, NEW_ARTIST_BIO_MAX).join("");

/* ---------- links ---------- */

/**
 * Applicants may type a URL, a bare domain or just a drama title. Returns an https/http href to open in a new tab,
 * or null when the entry should be shown as plain text (never javascript:/data: etc.).
 */
export function creatorLinkHref(link: string): string | null {
  const value = link.trim();
  if (!value || /\s/.test(value)) return null;
  const candidate = /^https?:\/\//i.test(value) ? value : /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$)/i.test(value) ? `https://${value}` : null;
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

/* ---------- errors ---------- */

const CREATOR_ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "請重新登入後操作",
  FORBIDDEN: "需要管理員或工作人員權限",
  NOT_FOUND: "找不到此申請或藝人（可能已被刪除）",
  INVALID_ARGUMENT: "請檢查欄位：拒絕原因至少 5 字、藝人名稱 ≤30 字、簡介 ≤150 字",
  CREATOR_APPLICATION_STATE: "此申請已處理，請重新整理",
  FAN_PASS_REQUEST_STATE: "此月卡申請已處理，請重新整理",
  FAN_PASS_REQUEST_APPLYING: "此月卡申請正在套用中，請稍候幾分鐘再重新整理",
  ARTIST_ALREADY_OWNED: "此藝人已有其他擁有者；若要加為共同藝人，請勾選確認後再送出",
  CREATOR_SELF_APPROVE: "不能核准自己的創作者申請，請由其他工作人員審核",
  HANDLE_TAKEN: "此 @handle 已被其他藝人使用，請換一個",
  TEMPORARILY_UNAVAILABLE: "服務暫時無法使用，請稍後再試",
  FEATURE_NOT_READY: "藝人專區尚未開放此環境",
};

export function creatorErrorMessage(code: string | undefined, reason?: string, status?: number): string {
  if (code === "INVALID_ARGUMENT" && reason === "handle") return HANDLE_RULE_TEXT;
  if (code && CREATOR_ERROR_MESSAGES[code]) return CREATOR_ERROR_MESSAGES[code];
  if (status === 404 && !code) return "後端尚未部署創作者申請（B-40）";
  if (code) return `操作未完成（${code}${reason ? `：${reason}` : ""}）`;
  return "操作未完成，請重試";
}

/* ---------- C-05: 鐵粉月卡自助申請（B-43, contract v2.7 — fields agreed ahead of the contract landing） ---------- */

export type FanPassRequestStatus = "pending" | "approved" | "approved_manual" | "rejected";
export const FAN_PASS_REQUEST_STATUS_LABELS: Record<FanPassRequestStatus, string> = {
  pending: "待審",
  approved: "已核准",
  approved_manual: "需人工",
  rejected: "已拒絕",
};
export const FAN_PASS_REQUEST_STATUS_ORDER: FanPassRequestStatus[] = ["pending", "approved", "approved_manual", "rejected"];
export const fanPassRequestStatusLabel = (s: string | null | undefined) =>
  s && s in FAN_PASS_REQUEST_STATUS_LABELS ? FAN_PASS_REQUEST_STATUS_LABELS[s as FanPassRequestStatus] : s || "—";
export const fanPassRequestStatusVariant = (s: string) =>
  s === "approved" ? "success" : s === "approved_manual" ? "info" : s === "rejected" ? "danger" : "warning";

/** The requested values; a field that is absent (undefined or null) is not part of the request. */
export interface FanPassRequestedValues {
  priceTier?: number | null;
  benefits?: string[] | null;
  saleEnabled?: boolean | null;
  welcomeText?: string | null;
}
export type FanPassCurrentValues = Partial<{ priceTier: number | null; benefits: string[]; saleEnabled: boolean; welcomeText: string | null }>;
export interface FanPassChangeLine {
  field: keyof FanPassRequestedValues;
  label: string;
  /** "—" when the current value is unknown or unset. */
  from: string;
  to: string;
  changed: boolean;
}

const tierText = (t: number | null | undefined) => (typeof t === "number" && Number.isFinite(t) ? `NT$${t}／月` : "—");
const saleText = (v: boolean | null | undefined) => (v === true ? "開賣" : v === false ? "停售" : "—");
const benefitsText = (b: string[] | null | undefined) => {
  const list = (b ?? []).map((x) => x.trim()).filter(Boolean);
  return list.length ? list.join("／") : "—";
};
const welcomeShort = (t: string | null | undefined) => {
  const v = (t ?? "").trim();
  if (!v) return "—";
  const chars = [...v];
  return chars.length > 60 ? `${chars.slice(0, 60).join("")}…` : v;
};

/**
 * 變更摘要：one line per requested field, "目前值 → 申請值". Pass `current` = {} (or omit fields) while the
 * current values are still loading; those lines show "—" and `changed` stays true.
 */
export function fanPassChangeSummary(request: FanPassRequestedValues, current: FanPassCurrentValues = {}): FanPassChangeLine[] {
  const lines: FanPassChangeLine[] = [];
  const push = (field: keyof FanPassRequestedValues, label: string, from: string, to: string, known: boolean) =>
    lines.push({ field, label, from, to, changed: !known || from !== to });
  if (request.priceTier != null) push("priceTier", "價格檔位", tierText(current.priceTier), tierText(request.priceTier), "priceTier" in current);
  if (request.saleEnabled != null)
    push("saleEnabled", "販售", saleText(current.saleEnabled), saleText(request.saleEnabled), "saleEnabled" in current);
  if (request.benefits != null) push("benefits", "權益文案", benefitsText(current.benefits), benefitsText(request.benefits), "benefits" in current);
  if (request.welcomeText != null)
    push("welcomeText", "歡迎訊息", welcomeShort(current.welcomeText), welcomeShort(request.welcomeText), "welcomeText" in current);
  return lines;
}

/** Text for the clipboard: numbered manual steps returned by an `approved_manual` approval. */
export const formatManualSteps = (steps: string[] | null | undefined, heading?: string) =>
  [...(heading ? [heading] : []), ...(steps ?? []).map((s, i) => `${i + 1}. ${s}`)].join("\n");

/* ---------- @handle ---------- */

export const HANDLE_PATTERN = /^[a-z0-9._]{3,20}$/;
export const HANDLE_RULE_TEXT = "@handle 須為 3–20 字，只能使用小寫英文、數字、底線與句點";
export const SHARE_HOST = "on-reel.com";

/** Trims, drops a leading "@" and lower-cases what staff typed. */
export const normalizeHandle = (input: string) => input.trim().replace(/^@+/, "").toLowerCase();
/** Error message, or null when the (normalized) handle is valid. */
export function handleProblem(input: string): string | null {
  const h = normalizeHandle(input);
  if (!h) return "請填寫 @handle";
  return HANDLE_PATTERN.test(h) ? null : HANDLE_RULE_TEXT;
}
export const handleShareUrl = (handle: string) => `https://${SHARE_HOST}/a/${encodeURIComponent(normalizeHandle(handle))}`;
